import React, { useState, useEffect } from 'react';
import { hrAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Building2, Plus, Search, Users, Edit2, Trash2, MapPin, Phone, Calendar, Award, FileText } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminHR() {
  const [tab, setTab] = useState('staff');
  const [departments, setDepartments] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedDept, setSelectedDept] = useState('');
  const [showDeptDialog, setShowDeptDialog] = useState(false);
  const [showStaffDialog, setShowStaffDialog] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [editingStaff, setEditingStaff] = useState(null);
  const [deptForm, setDeptForm] = useState({ name: '', slug: '', description: '' });
  const [staffForm, setStaffForm] = useState({ department_id: '', position: '', phone: '', location: '', skills: '', bio_extended: '' });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [search, selectedDept]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [deptRes, staffRes] = await Promise.all([
        hrAPI.departments(),
        hrAPI.staff({ search, department_id: selectedDept || undefined, limit: 50 })
      ]);
      setDepartments(deptRes.data.departments || []);
      setStaff(staffRes.data.staff || []);
    } catch { toast.error('Failed to load HR data'); }
    finally { setLoading(false); }
  };

  const saveDepartment = async () => {
    if (!deptForm.name) { toast.error('Name is required'); return; }
    try {
      if (editingDept) {
        await hrAPI.updateDepartment(editingDept.id, deptForm);
        toast.success('Department updated');
      } else {
        await hrAPI.createDepartment({ ...deptForm, slug: deptForm.slug || deptForm.name.toLowerCase().replace(/\s+/g, '-') });
        toast.success('Department created');
      }
      setShowDeptDialog(false); setEditingDept(null);
      setDeptForm({ name: '', slug: '', description: '' });
      loadData();
    } catch { toast.error('Failed to save department'); }
  };

  const deleteDepartment = async (id) => {
    try { await hrAPI.deleteDepartment(id); toast.success('Deleted'); loadData(); }
    catch { toast.error('Failed to delete'); }
  };

  const saveStaffProfile = async () => {
    if (!editingStaff) return;
    try {
      const data = { ...staffForm, skills: staffForm.skills ? staffForm.skills.split(',').map(s => s.trim()) : [] };
      await hrAPI.updateStaff(editingStaff.id, data);
      toast.success('Staff profile updated');
      setShowStaffDialog(false); setEditingStaff(null);
      loadData();
    } catch { toast.error('Failed to update'); }
  };

  const openEditStaff = (s) => {
    setEditingStaff(s);
    setStaffForm({
      department_id: s.department_id || '',
      position: s.position || '',
      phone: s.phone || '',
      location: s.location || '',
      skills: (s.skills || []).join(', '),
      bio_extended: s.bio_extended || ''
    });
    setShowStaffDialog(true);
  };

  const ROLE_COLORS = {
    super_admin: '#7C3AED', admin: '#B3261E', editor: '#1E5A8A',
    producer: '#0891B2', journalist: '#1F7A5A', photographer: '#059669',
    designer: '#DB2777', data_journalist: '#7C3AED', social_media_manager: '#0284C7',
    seo_specialist: '#EA580C', ad_operations: '#CA8A04', subscription_manager: '#0D9488',
    analytics_analyst: '#4F46E5', hr_admin: '#65A30D', finance_controller: '#D97706',
    legal_counsel: '#E11D48', external_contributor: '#6B7280', moderator: '#9A6B1A',
    reader: '#5B616E',
  };

  return (
    <div className="space-y-6" data-testid="admin-hr-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Building2 className="w-6 h-6 text-[#B89B5E]" /> HR & Staff Management</h1>
          <p className="text-sm text-[#5B616E] mt-1">Manage departments, staff profiles, and organizational structure</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="staff">Staff Directory</TabsTrigger>
          <TabsTrigger value="departments">Departments</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="staff" className="mt-4 space-y-4">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5B616E]" />
              <Input className="pl-10" placeholder="Search staff..." value={search} onChange={e => setSearch(e.target.value)} data-testid="staff-search-input" />
            </div>
            <Select value={selectedDept} onValueChange={setSelectedDept}>
              <SelectTrigger className="w-48"><SelectValue placeholder="All departments" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-48 rounded-xl" />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {staff.map(s => (
                <Card key={s.id} className="bg-white border-[#E7E0D6] hover:shadow-md transition-shadow cursor-pointer" onClick={() => openEditStaff(s)} data-testid="staff-card">
                  <CardContent className="pt-5">
                    <div className="flex items-start gap-3">
                      {s.avatar_url ? (
                        <img src={s.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-[#B89B5E]/10 flex items-center justify-center text-lg font-bold" style={{ color: ROLE_COLORS[s.role] || '#5B616E' }}>
                          {s.full_name?.charAt(0) || '?'}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{s.full_name}</p>
                        <p className="text-xs text-[#5B616E] truncate">{s.email}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="text-[10px]" style={{ borderColor: ROLE_COLORS[s.role], color: ROLE_COLORS[s.role] }}>{s.role?.replace('_', ' ')}</Badge>
                          {s.position && <span className="text-[10px] text-[#5B616E]">{s.position}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-[#E7E0D6] grid grid-cols-2 gap-2 text-xs text-[#5B616E]">
                      <div className="flex items-center gap-1"><FileText className="w-3 h-3" /> {s.article_count || 0} articles</div>
                      {s.location && <div className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {s.location}</div>}
                      {s.department?.name && <div className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {s.department.name}</div>}
                      {s.last_activity && <div className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Active {new Date(s.last_activity).toLocaleDateString()}</div>}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {staff.length === 0 && <div className="col-span-full text-center py-12 text-[#5B616E]">No staff members found</div>}
            </div>
          )}
        </TabsContent>

        <TabsContent value="departments" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => { setDeptForm({ name: '', slug: '', description: '' }); setEditingDept(null); setShowDeptDialog(true); }} data-testid="create-dept-btn" className="bg-[#121316] text-white hover:bg-[#1B1D22]">
              <Plus className="w-4 h-4 mr-2" /> New Department
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {departments.map(d => (
              <Card key={d.id} className="bg-white border-[#E7E0D6]">
                <CardContent className="pt-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium">{d.name}</h3>
                      <p className="text-xs text-[#5B616E] mt-1">{d.description || 'No description'}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setDeptForm({ name: d.name, slug: d.slug, description: d.description || '' }); setEditingDept(d); setShowDeptDialog(true); }}><Edit2 className="w-3 h-3" /></Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#B3261E]" onClick={() => deleteDepartment(d.id)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#E7E0D6]">
                    <Users className="w-4 h-4 text-[#5B616E]" />
                    <span className="text-sm text-[#5B616E]">{d.staff_count || 0} members</span>
                  </div>
                </CardContent>
              </Card>
            ))}
            {departments.length === 0 && <div className="col-span-full text-center py-12 text-[#5B616E]">No departments created yet</div>}
          </div>
        </TabsContent>

        <TabsContent value="activity" className="mt-4">
          <ActivityLog />
        </TabsContent>
      </Tabs>

      {/* Department Dialog */}
      <Dialog open={showDeptDialog} onOpenChange={setShowDeptDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{editingDept ? 'Edit Department' : 'New Department'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-sm font-medium mb-1 block">Name</label><Input value={deptForm.name} onChange={e => setDeptForm({ ...deptForm, name: e.target.value })} data-testid="dept-name-input" /></div>
            <div><label className="text-sm font-medium mb-1 block">Slug</label><Input value={deptForm.slug} onChange={e => setDeptForm({ ...deptForm, slug: e.target.value })} placeholder="auto-generated" /></div>
            <div><label className="text-sm font-medium mb-1 block">Description</label><Textarea value={deptForm.description} onChange={e => setDeptForm({ ...deptForm, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDeptDialog(false)}>Cancel</Button>
            <Button onClick={saveDepartment} data-testid="save-dept-btn" className="bg-[#121316] text-white hover:bg-[#1B1D22]">{editingDept ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Staff Profile Dialog */}
      <Dialog open={showStaffDialog} onOpenChange={setShowStaffDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Edit Staff Profile: {editingStaff?.full_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-sm font-medium mb-1 block">Department</label>
              <Select value={staffForm.department_id} onValueChange={v => setStaffForm({ ...staffForm, department_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><label className="text-sm font-medium mb-1 block">Position/Title</label><Input value={staffForm.position} onChange={e => setStaffForm({ ...staffForm, position: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-sm font-medium mb-1 block">Phone</label><Input value={staffForm.phone} onChange={e => setStaffForm({ ...staffForm, phone: e.target.value })} /></div>
              <div><label className="text-sm font-medium mb-1 block">Location</label><Input value={staffForm.location} onChange={e => setStaffForm({ ...staffForm, location: e.target.value })} /></div>
            </div>
            <div><label className="text-sm font-medium mb-1 block">Skills (comma separated)</label><Input value={staffForm.skills} onChange={e => setStaffForm({ ...staffForm, skills: e.target.value })} placeholder="journalism, editing, photography" /></div>
            <div><label className="text-sm font-medium mb-1 block">Bio</label><Textarea value={staffForm.bio_extended} onChange={e => setStaffForm({ ...staffForm, bio_extended: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowStaffDialog(false)}>Cancel</Button>
            <Button onClick={saveStaffProfile} data-testid="save-staff-btn" className="bg-[#121316] text-white hover:bg-[#1B1D22]">Update Profile</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ActivityLog() {
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    hrAPI.activity({ days: 7, limit: 50 }).then(r => setActivity(r.data.activity || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-12" />)}</div>;

  return (
    <Card className="bg-white border-[#E7E0D6]">
      <CardHeader><CardTitle className="text-sm font-medium">Recent Activity (7 days)</CardTitle></CardHeader>
      <CardContent>
        <div className="space-y-3">
          {activity.map((a, i) => (
            <div key={i} className="flex items-center gap-3 text-sm">
              <div className="w-2 h-2 rounded-full bg-[#B89B5E]" />
              <div className="flex-1">
                <span className="font-medium">{a.user_email || a.user_id}</span>
                <span className="text-[#5B616E]"> {a.action} </span>
                <span className="text-[#5B616E]">{a.resource_type}</span>
              </div>
              <span className="text-xs text-[#5B616E]">{a.created_at ? new Date(a.created_at).toLocaleString() : ''}</span>
            </div>
          ))}
          {activity.length === 0 && <p className="text-sm text-[#5B616E] text-center py-4">No recent activity</p>}
        </div>
      </CardContent>
    </Card>
  );
}
